// What a single input will accept. A message that names a domain concept
// ("Die Raum-ID darf nicht leer sein") belongs to that concept's namespace
// under `errors.*`, not here.
import type {
  FieldErrorCode,
  ServerErrorParams,
} from '../../constants/server-error-code'
import type {
  ExtraFieldKey,
  ValidatedField,
} from '../../constants/validated-field'

import { COMMON } from './common'
import { CONFIGURATOR_CMS } from './configurator-cms'
import { DOCUMENTS } from './documents'
import { FIELD_LABELS } from './fields'
import { FINANCE } from './finance'
import { ROOMS } from './rooms'
import { SYSTEM_SETTINGS } from './system-settings'
import { TERMS } from './terms'

export const VALIDATION = {
  invalid: {
    email: 'Ungültige E-Mail-Adresse',
    url: 'Ungültige URL',
    postalCode: 'PLZ muss 5 Ziffern haben',
    year: 'Ungültiges Jahr (4 Ziffern)',
    imageUrl: 'Ungültige Bild-URL (erlaubt: .jpg, .png, .webp)',
  },
  required: 'Pflichtfeld',
} as const

/**
 * Names for the inputs the Internal forms carry that are not a field of an
 * entity — a technical key, a sort index, a range bound. `fieldName` reads
 * `FIELD_LABELS` first, so anything that IS an entity field keeps its central
 * label and cannot be spelled twice.
 */
const KEY = 'Key'

const EXTRA_FIELD_NAMES = {
  key: KEY,
  label: COMMON.designation,
  sortOrder: 'Sortierung',
  minValue: 'Minimalwert',
  maxValue: 'Maximalwert',
  decimalPlaces: COMMON.decimalPlaces,
  kind: FIELD_LABELS.type.short,
  measureId: TERMS.measure,
  floorType: ROOMS.floor.modal.typeLabel,
  direction: FINANCE.invoices.side,
  settlement: 'Art des Abzugs',
  roomTypes: CONFIGURATOR_CMS.groupKinds.ROOM,
  floorTypes: CONFIGURATOR_CMS.groupKinds.FLOOR,
  optionKey: `Options-${KEY}`,
  measureCode: 'Maßnahmen-Code',
  formulaName: 'Funktionsname',
  optionLabel: `Options-${COMMON.designation}`,
  optionSortOrder: 'Options-Sortierung',
  roomCode: TERMS.roomCode,
  summaryIcon: 'Icon der Zusammenfassung',
  summaryUnit: 'Einheit der Zusammenfassung',
  summaryScope: 'Gültigkeit der Zusammenfassung',
  summaryCustomerVisible: COMMON.customerVisible,
  filename: 'Dateiname',
  tags: DOCUMENTS.tagsLabel,
  fileSize: DOCUMENTS.fileSize,
  mimeType: 'Dateityp',
  // A technical subsystem keeps its own vocabulary; the key is what the log
  // and the request carry.
  s3Key: 'S3 Key',
  batchSize: 'Batch-Größe',
  // A technical subsystem keeps its own vocabulary: these name the provider's
  // own concepts, which the request and the system-area page carry as such.
  aiTask: 'AI-Task',
  aiProvider: 'AI-Provider',
  aiModel: 'AI-Model',
  imageTargetWidth: 'Zielbreite',
  imageDensities: 'Densities',
  cursorPosition: 'Cursor-Position',
  intervalMinutes: SYSTEM_SETTINGS.email.intervalLabel,
  placementIds: 'Abgewählte Elemente',
  pictureUuids: 'Bilderreihenfolge',
  priceAdjustment: 'Preisanpassung',
  adjustmentValue: 'Wert der Preisanpassung',
  locked: 'Sperre',
  /** What somebody wrote in a comment. */
  content: 'Kommentar',
  stampPage: 'Stempelseite',
  stampX: 'Stempelposition (X)',
  stampY: 'Stempelposition (Y)',
  stampScale: 'Stempelgröße',
} as const satisfies Record<ExtraFieldKey, string>

/** What to call the input a validation failure is about. */
export function fieldName(name: ValidatedField): string {
  const label = (FIELD_LABELS as Record<string, { label: string } | undefined>)[
    name
  ]?.label
  return label ?? (EXTRA_FIELD_NAMES as Record<string, string>)[name]
}

/**
 * The German for each `FIELD_*` code, keyed by the code so `SERVER_ERROR_TEXT`
 * can spread it — the sentence is a function of the code and its params and
 * needs no forwarding lambda per member.
 */
export const FIELD_ERRORS = {
  FIELD_REQUIRED: p => `${fieldName(p.field)} ist ein Pflichtfeld.`,
  FIELD_EMPTY: p => `${fieldName(p.field)} darf nicht leer sein.`,
  FIELD_TOO_LONG: p =>
    `${fieldName(p.field)} darf höchstens ${p.max} Zeichen lang sein.`,
  FIELD_NOT_FINITE: p => `${fieldName(p.field)} muss eine Zahl sein.`,
  FIELD_OUT_OF_RANGE: p =>
    `${fieldName(p.field)} muss zwischen ${p.min} und ${p.max} liegen.`,
  FIELD_OUT_OF_OPEN_RANGE: p =>
    `${fieldName(p.field)} muss größer als ${p.min} und höchstens ${p.max} sein.`,
  FIELD_NEGATIVE: p => `${fieldName(p.field)} darf nicht negativ sein.`,
  FIELD_NOT_NON_NEGATIVE_INTEGER: p =>
    `${fieldName(p.field)} muss eine ganze Zahl ≥ 0 sein.`,
  FIELD_NOT_INTEGER_AT_LEAST: p =>
    `${fieldName(p.field)} muss eine ganze Zahl ≥ ${p.min} sein.`,
  FIELD_VALUE_INVALID: p =>
    `Ungültiger Wert für ${fieldName(p.field)}: „${p.value}“.`,
  FIELD_DUPLICATE: p =>
    `${fieldName(p.field)} „${p.value}“ ist bereits vergeben.`,
  FIELD_NOT_CAMEL_CASE: p =>
    `${fieldName(p.field)} muss in camelCase geschrieben sein (Kleinbuchstabe am Anfang, keine Sonderzeichen).`,
  FIELD_NOT_SCREAMING_SNAKE_CASE: p =>
    `${fieldName(p.field)} muss in GROSSBUCHSTABEN mit Unterstrichen geschrieben sein.`,
} satisfies {
  [C in FieldErrorCode]: (params: ServerErrorParams[C]) => string
}

/** Said about the pair, not about one field. */
export const RANGE_MIN_ABOVE_MAX = `${fieldName('minValue')} darf nicht größer als ${fieldName('maxValue')} sein.`

export const decimalPlacesNotAllowed = (allowed: string) =>
  `${fieldName('decimalPlaces')} muss einer dieser Werte sein: ${allowed}.`
